/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as moment from 'moment';
import 'moment-timezone';

type TimeShifterLike = {
  getNetworkTimezone(): string;
  getMomentInNetworkTimezone(theDate: any): moment.Moment;
};

export function getNetworkTimezone(adnTimeShifter: TimeShifterLike): string {
  return adnTimeShifter.getNetworkTimezone() || 'utc';
}

export function pickerDateFromApiInstant(apiDate: Date | string | moment.Moment, adnTimeShifter: TimeShifterLike): Date {
  const networkMoment = adnTimeShifter.getMomentInNetworkTimezone(apiDate);
  return new Date(
    networkMoment.year(),
    networkMoment.month(),
    networkMoment.date(),
    networkMoment.hour(),
    networkMoment.minute(),
    networkMoment.second(),
    networkMoment.millisecond()
  );
}

export function apiInstantFromPickerDate(pickerDate: Date, adnTimeShifter: TimeShifterLike): Date {
  const timeZone = getNetworkTimezone(adnTimeShifter);
  const localMoment = moment(pickerDate);
  return moment.tz({
    year: localMoment.year(),
    month: localMoment.month(),
    date: localMoment.date(),
    hour: localMoment.hour(),
    minute: localMoment.minute(),
    second: localMoment.second(),
    millisecond: localMoment.millisecond()
  }, timeZone).toDate();
}

export function networkDayFromPickerDate(pickerDate: Date, adnTimeShifter: TimeShifterLike): string {
  const timeZone = getNetworkTimezone(adnTimeShifter);
  const localMoment = moment(pickerDate);
  return moment.tz({
    year: localMoment.year(),
    month: localMoment.month(),
    date: localMoment.date(),
    hour: localMoment.hour(),
    minute: localMoment.minute(),
    second: localMoment.second()
  }, timeZone).format('YYYY-MM-DD');
}

export function pickerDateFromNetworkDay(day: string, timeZone: string, boundary: 'start' | 'end' = 'start'): Date {
  const dayMoment = moment.tz(day, 'YYYY-MM-DD', timeZone);
  const bounded = boundary === 'start' ? dayMoment.startOf('day') : dayMoment.endOf('day');
  return new Date(
    bounded.year(),
    bounded.month(),
    bounded.date(),
    bounded.hour(),
    bounded.minute(),
    bounded.second(),
    bounded.millisecond()
  );
}

export function pickerDateFromApiDay(
  apiDate: Date | string | null | undefined,
  adnTimeShifter: TimeShifterLike,
  boundary: 'start' | 'end' = 'start'
): Date | undefined {
  if (!apiDate) {
    return undefined;
  }
  const day = adnTimeShifter.getMomentInNetworkTimezone(apiDate).format('YYYY-MM-DD');
  return pickerDateFromNetworkDay(day, getNetworkTimezone(adnTimeShifter), boundary);
}

export function apiInstantFromPickerDateBoundary(
  pickerDate: Date | null | undefined,
  adnTimeShifter: TimeShifterLike,
  boundary: 'start' | 'end'
): Date | null {
  if (!pickerDate) {
    return null;
  }
  const day = networkDayFromPickerDate(pickerDate, adnTimeShifter);
  const dayMoment = moment.tz(day, 'YYYY-MM-DD', getNetworkTimezone(adnTimeShifter));
  return (boundary === 'start' ? dayMoment.startOf('day') : dayMoment.endOf('day')).toDate();
}

export function formatNetworkDateTime(value: Date | string, timeZone: string, format: string): string {
  return moment(value).tz(timeZone).format(format);
}

export function networkTimeFromPickerDate(pickerDate: Date, timeZone: string): string {
  const localMoment = moment(pickerDate);
  return moment.tz({
    year: 2020,
    month: 1,
    day: 1,
    hour: localMoment.hour(),
    minute: localMoment.minute(),
    second: localMoment.second()
  }, timeZone).format('HH:mm:ss');
}

export function pickerDateFromNetworkTime(time: string, timeZone: string): Date {
  const networkMoment = moment.tz('2020-02-01 ' + time, timeZone);
  return new Date(
    networkMoment.year(),
    networkMoment.month(),
    networkMoment.date(),
    networkMoment.hour(),
    networkMoment.minute(),
    networkMoment.second(),
    networkMoment.millisecond()
  );
}

export function savedValueToNetworkDay(value: any, timeZone: string): string | null {
  if (!value) {
    return null;
  }
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  return moment(value).tz(timeZone).format('YYYY-MM-DD');
}
